import React, {
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { FilterContext } from "../../context/Filter";
import {
  getDealerNames,
  getLob,
  getpl,
  getPpl,
  getYears,
  stateList,
} from "../../pages/Vehicle/VechicleKpi";

const Filter = () => {
  const {
    arn: parArn,
    childArn,
    vehiclePayload,
    setVehiclePayload,
    vehicleFilterPayload,
    setVehicleFilterPayload,
  } = useContext(FilterContext);

  const syncInProgress = useRef(false);
  const hasInitialized = useRef(false);

  const [states, setStates] = useState([]);
  const [years, setYears] = useState([]);
  const [dealer, setDealer] = useState([]);
  const [lob, setLob] = useState([]);
  const [ppl, setPpl] = useState([]);
  const [pl, setPl] = useState([]);

  const [selectedState, setSelectedState] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedDealer, setSelectedDealer] = useState("");
  const [selectedLob, setSelectedLob] = useState("");
  const [selectedPpl, setSelectedPpl] = useState("");
  const [selectedPl, setSelectedPl] = useState("");

  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [updatingDropdown, setUpdatingDropdown] = useState(null);

  const shouldEnableQuery = Boolean(parArn || childArn);

  const cleanPayload = (payload) => {
    const cleaned = { ...payload };
    Object.keys(cleaned).forEach((key) => {
      if (Array.isArray(cleaned[key]) && cleaned[key].length === 0) {
        delete cleaned[key];
      }
    });
    return cleaned;
  };

  // Sync from context
  useEffect(() => {
    if (vehicleFilterPayload && !syncInProgress.current) {
      syncInProgress.current = true;
      setSelectedState(vehicleFilterPayload.state?.[0] || "");
      setSelectedYear(vehicleFilterPayload.fscl_year?.[0] || "");
      setSelectedDealer(vehicleFilterPayload.dealer?.[0] || "");
      setSelectedLob(vehicleFilterPayload.lob?.[0] || "");
      setSelectedPpl(vehicleFilterPayload.ppl?.[0] || "");
      setSelectedPl(vehicleFilterPayload.pl?.[0] || "");
      setTimeout(() => {
        syncInProgress.current = false;
      }, 100);
    }
  }, [vehicleFilterPayload]);

  // Generate payload
  const generatePayload = useCallback(() => {
    const payload = {
      ...(childArn ? { childArn } : { parArn }),
      // don’t add search_type and dropDownValue by default
    };

    if (selectedState) payload.state = [selectedState];
    if (selectedYear) payload.fscl_year = [selectedYear];
    if (selectedDealer) payload.dealer = [selectedDealer];
    if (selectedLob) payload.lob = [selectedLob];
    if (selectedPpl) payload.ppl = [selectedPpl];
    if (selectedPl) payload.pl = [selectedPl];

    return cleanPayload(payload);
  }, [
    parArn,
    childArn,
    selectedState,
    selectedYear,
    selectedDealer,
    selectedLob,
    selectedPpl,
    selectedPl,
  ]);

  // ===== UPDATE ALL DROPDOWNS LOGIC =====
  const updateAllDropdowns = useCallback(
    async (changedField, newValue) => {
      setUpdatingDropdown("all");

      // Create payload with current selections including the new value
      const currentPayload = {
        ...(childArn ? { childArn } : { parArn }),
        search_type: [],
        dropDownValue: [],
      };

      // Add current selections to payload
      if (changedField === "state" && newValue)
        currentPayload.state = [newValue];
      else if (selectedState) currentPayload.state = [selectedState];

      if (changedField === "fscl_year" && newValue)
        currentPayload.fscl_year = [newValue];
      else if (selectedYear) currentPayload.fscl_year = [selectedYear];

      if (changedField === "dealer" && newValue)
        currentPayload.dealer = [newValue];
      else if (selectedDealer) currentPayload.dealer = [selectedDealer];

      if (changedField === "lob" && newValue) currentPayload.lob = [newValue];
      else if (selectedLob) currentPayload.lob = [selectedLob];

      if (changedField === "ppl" && newValue) currentPayload.ppl = [newValue];
      else if (selectedPpl) currentPayload.ppl = [selectedPpl];

      if (changedField === "pl" && newValue) currentPayload.pl = [newValue];
      else if (selectedPl) currentPayload.pl = [selectedPl];

      try {
        // Always fetch ALL dropdown options when any selection changes
        const [stateData, yearData, dealerData, lobData, pplData, plData] =
          await Promise.all([
            stateList(parArn, childArn, currentPayload),
            getYears(parArn, childArn, currentPayload),
            getDealerNames(parArn, childArn, currentPayload),
            getLob(parArn, childArn, currentPayload),
            getPpl(parArn, childArn, currentPayload),
            getpl(parArn, childArn, currentPayload),
          ]);

        // Update ALL dropdown options
        setStates(stateData || []);
        setYears(yearData || []);
        setDealer(dealerData || []);
        setLob(lobData || []);
        setPpl(pplData || []);
        setPl(plData || []);

        // Validate and reset selections that are no longer available
        validateAndResetSelections(
          [stateData, yearData, dealerData, lobData, pplData, plData],
          changedField
        );
      } catch (error) {
        console.error(
          `Failed to update all dropdowns after ${changedField} change:`,
          error
        );
      } finally {
        setUpdatingDropdown(null);
      }
    },
    [
      parArn,
      childArn,
      selectedState,
      selectedYear,
      selectedDealer,
      selectedLob,
      selectedPpl,
      selectedPl,
    ]
  );

  // Helper function to validate and reset selections that are no longer available
  const validateAndResetSelections = useCallback(
    (results, changedField) => {
      const [stateData, yearData, dealerData, lobData, pplData, plData] =
        results;

      // Don't reset the field that was just changed
      if (
        changedField !== "state" &&
        selectedState &&
        !stateData?.some((i) => i.key === selectedState)
      ) {
        setSelectedState("");
      }

      if (
        changedField !== "fscl_year" &&
        selectedYear &&
        !yearData?.some((i) => i.key === selectedYear)
      ) {
        setSelectedYear("");
      }

      if (
        changedField !== "dealer" &&
        selectedDealer &&
        !dealerData?.some((i) => i.key === selectedDealer)
      ) {
        setSelectedDealer("");
      }

      if (
        changedField !== "lob" &&
        selectedLob &&
        !lobData?.some((i) => i.key === selectedLob)
      ) {
        setSelectedLob("");
      }

      if (
        changedField !== "ppl" &&
        selectedPpl &&
        !pplData?.some((i) => i.key === selectedPpl)
      ) {
        setSelectedPpl("");
      }

      if (
        changedField !== "pl" &&
        selectedPl &&
        !plData?.some((i) => i.key === selectedPl)
      ) {
        setSelectedPl("");
      }
    },
    [
      selectedState,
      selectedYear,
      selectedDealer,
      selectedLob,
      selectedPpl,
      selectedPl,
    ]
  );

  // Enhanced dropdown change handlers - each updates ALL dropdowns
  const handleStateChange = useCallback(
    (value) => {
      setSelectedState(value);
      if (value) {
        updateAllDropdowns("state", value);
      }
    },
    [updateAllDropdowns]
  );

  const handleYearChange = useCallback(
    (value) => {
      setSelectedYear(value);
      if (value) {
        updateAllDropdowns("fscl_year", value);
      }
    },
    [updateAllDropdowns]
  );

  const handleDealerChange = useCallback(
    (value) => {
      setSelectedDealer(value);
      if (value) {
        updateAllDropdowns("dealer", value);
      }
    },
    [updateAllDropdowns]
  );

  const handleLobChange = useCallback(
    (value) => {
      setSelectedLob(value);
      if (value) {
        updateAllDropdowns("lob", value);
      }
    },
    [updateAllDropdowns]
  );

  const handlePplChange = useCallback(
    (value) => {
      setSelectedPpl(value);
      if (value) {
        updateAllDropdowns("ppl", value);
      }
    },
    [updateAllDropdowns]
  );

  const handlePlChange = useCallback(
    (value) => {
      setSelectedPl(value);
      if (value) {
        updateAllDropdowns("pl", value);
      }
    },
    [updateAllDropdowns]
  );

  // Submit
  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (isSubmitting || syncInProgress.current || updatingDropdown) return;

      setIsSubmitting(true);
      syncInProgress.current = true;

      const payload = generatePayload();
      setTimeout(() => {
        setVehicleFilterPayload(payload);
        setVehiclePayload(payload);
        setTimeout(() => {
          setIsSubmitting(false);
          syncInProgress.current = false;
        }, 500);
      }, 0);
    },
    [
      generatePayload,
      isSubmitting,
      updatingDropdown,
      setVehicleFilterPayload,
      setVehiclePayload,
    ]
  );

  const handleReset = useCallback(() => {
    if (syncInProgress.current || updatingDropdown) return;

    syncInProgress.current = true;

    const resetPayload = cleanPayload({
      ...(childArn ? { childArn } : { parArn }),
      // don’t add search_type/dropDownValue here
    });

    // reset selections
    setSelectedState("");
    setSelectedYear("");
    setSelectedDealer("");
    setSelectedLob("");
    setSelectedPpl("");
    setSelectedPl("");

    setVehicleFilterPayload(resetPayload);
    setVehiclePayload(resetPayload);

    setTimeout(() => {
      updateAllDropdowns("reset", null);
      setTimeout(() => {
        syncInProgress.current = false;
      }, 500);
    }, 0);
  }, [
    parArn,
    childArn,
    updatingDropdown,
    setVehicleFilterPayload,
    setVehiclePayload,
    updateAllDropdowns,
  ]);

  // Initialize dropdowns
  useEffect(() => {
    if (shouldEnableQuery && !hasInitialized.current) {
      hasInitialized.current = true;
      setLoading(true);
      updateAllDropdowns("init", null).finally(() => {
        setLoading(false);
      });
    }
  }, [shouldEnableQuery, updateAllDropdowns]);

  if (loading) {
    return (
      <div className="max-w-full mx-auto p-6 bg-white rounded-md shadow-md flex items-center justify-center">
        <div className="text-gray-500">Loading filters...</div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-full mx-auto p-6 bg-white rounded-md shadow-md flex flex-col gap-2 flex-wrap"
    >
      <div className="flex">
        <Dropdown
          label="State"
          value={selectedState}
          onChange={handleStateChange}
          options={states}
          loading={updatingDropdown === "all"}
        />
        <Dropdown
          label="Dealer"
          value={selectedDealer}
          onChange={handleDealerChange}
          options={dealer}
          loading={updatingDropdown === "all"}
        />
        <Dropdown
          label="Year"
          value={selectedYear}
          onChange={handleYearChange}
          options={years}
          loading={updatingDropdown === "all"}
        />
        <Dropdown
          label="LOB"
          value={selectedLob}
          onChange={handleLobChange}
          options={lob}
          loading={updatingDropdown === "all"}
        />
        <Dropdown
          label="PPL"
          value={selectedPpl}
          onChange={handlePplChange}
          options={ppl}
          loading={updatingDropdown === "all"}
        />
        <Dropdown
          label="PL"
          value={selectedPl}
          onChange={handlePlChange}
          options={pl}
          loading={updatingDropdown === "all"}
        />
      </div>
      <div className="flex gap-2 justify-center items-center">
        <button
          type="submit"
          disabled={isSubmitting || updatingDropdown}
          className={`px-2 py-1 rounded-md transition ${
            isSubmitting || updatingDropdown
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-[#2374BF] hover:bg-indigo-700"
          } text-white`}
        >
          {isSubmitting ? "Submitting..." : "Submit"}
        </button>
        <div className="flex">
          <button
            type="button"
            onClick={handleReset}
            disabled={isSubmitting || updatingDropdown}
            className="bg-gray-400 text-white px-2 py-1 rounded-md hover:bg-gray-700 transition disabled:cursor-not-allowed"
          >
            Reset
          </button>
        </div>
      </div>
    </form>
  );
};

const Dropdown = React.memo(
  ({ label, value, onChange, options, loading = false }) => (
    <div className="flex gap-2 items-center text-xs">
      <label>{label}:</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={loading}
        className={`border border-gray-800 rounded-md py-1 px-2 ${
          loading ? "bg-gray-100 cursor-not-allowed" : ""
        }`}
      >
        <option value="">
          {loading ? `Loading ${label}...` : `Select ${label}`}
        </option>
        {options?.map((item) => (
          <option key={item.key || item.value} value={item.key || item.value}>
            {item.key || item.label}
          </option>
        ))}
      </select>
    </div>
  )
);

Dropdown.displayName = "Dropdown";

export default Filter;
